import * as React from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { fetchTherapistDashboard } from "../../Services/apiService";
import { useTranslation } from "../../i18n/LanguageContext";
import { C, cardSx } from "../../theme/dashboardCardTokens";
import StatCard from "../../components/portal/StatCard";
import StatCardDark from "../../components/portal/StatCardDark";
import PortalCardHeader from "../../components/portal/PortalCardHeader";
import PortalPageHeader from "../../components/portal/PortalPageHeader";

dayjs.extend(relativeTime);

const iconSx = { fontSize: 15, color: C.primary };
const iconSxDark = { fontSize: 15, color: "#fff" };

const SUMMARY_CARDS = [
  { key: "total_patients", labelKey: "totalPatients", Icon: PeopleAltOutlinedIcon },
  { key: "total_tests", labelKey: "totalTests", Icon: ScienceOutlinedIcon },
  { key: "completed_this_month", labelKey: "completedThisMonth", Icon: CheckCircleOutlineIcon },
  { key: "in_progress", labelKey: "inProgress", Icon: PendingActionsOutlinedIcon },
];

// Therapist portal home — summary counts across every patient linked to
// this therapist, plus the last 5 status changes across their tests.
// Patient/test detail lives on My Patients. Card styling mirrors the admin
// dashboard's bento cards (StatCardLight/CardHeader/ActivityRow in
// pages/admin/Dashboard.js) via the shared tokens in theme/dashboardCardTokens.
function TherapistDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    fetchTherapistDashboard()
      .then((res) => {
        if (!cancelled) setData(res.data || {});
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) {
          navigate("/dashboard", { replace: true });
          return;
        }
        setError(err.message || t("portal.dashboard.therapist.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ color: C.ink }} />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  const recentActivity = data?.recent_activity || [];

  return (
    <Box>
      <PortalPageHeader
        title={t("portal.dashboard.therapist.pageTitle")}
        subtitle={t("portal.dashboard.therapist.pageSubtitle")}
      />

      <Box sx={{ display: "flex", gap: "20px", flexWrap: "wrap", mb: "20px" }}>
        {SUMMARY_CARDS.map((card, idx) => {
          const raw = data?.[card.key];
          const value = card.format ? card.format(raw) : raw ?? 0;
          const label = t(`portal.dashboard.therapist.summary.${card.labelKey}`);
          return idx === 0 ? (
            <StatCardDark key={card.key} icon={<card.Icon sx={iconSxDark} />} value={value} label={label} />
          ) : (
            <StatCard key={card.key} icon={<card.Icon sx={iconSx} />} value={value} label={label} />
          );
        })}
      </Box>

      <Box sx={{ ...cardSx, p: "26px 28px" }}>
        <PortalCardHeader
          title={t("portal.dashboard.recentActivity.title")}
          subtitle={t("portal.dashboard.recentActivity.subtitle")}
        />

        {recentActivity.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: C.textFaint, textAlign: "center", py: 3 }}>
            {t("portal.dashboard.recentActivity.empty")}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {recentActivity.map((activity, idx) => (
              <Box
                key={`${activity.lab_test_id}-${activity.changed_at}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  p: "14px",
                  borderRadius: "14px",
                  background: idx % 2 === 0 ? "#FAFAFA" : "#FFFFFF",
                }}
              >
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: C.gray2, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 600, lineHeight: 1.4 }}>
                    {activity.substrate} · {activity.test_identifier}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: "5px", flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 12, color: C.textFaint }}>
                      {activity.changed_at ? dayjs(activity.changed_at).fromNow() : "—"}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.primary,
                        background: C.tintBg,
                        px: "9px",
                        py: "3px",
                        borderRadius: "999px",
                      }}
                    >
                      {t(`portal.statusTimeline.steps.${activity.step_key}.label`)}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default TherapistDashboard;
